import UpdateIcon from '@mui/icons-material/Update';
import {
  ReadOnlyAxlesRow,
  ReadOnlyAxlesTable,
} from '@src/axles/ReadOnlyAxlesTable';
import { colors } from '@src/common/styles/valueTokens';
import type { Order } from '@src/orders/domain/Order';
import type {
  EntryValue,
  PermitApplication,
} from '@src/orders/domain/PermitApplication';
import { AxleFormName } from '@src/permitField/AxleInformationFields';
import { TractorAxlesFormUtils } from '@src/permitField/TractorAxlesFormUtils';
import { TrailersFormName } from '@src/permitField/TrailerInformationFields';
import { CategorySection } from '@src/permits/CategorySection';
import { useTranslation } from 'react-i18next';

export const AxlesConfigurationCategory = ({
  isMetric,
  permitApplication,
  order,
}: {
  isMetric: boolean;
  permitApplication: PermitApplication;
  order: Order;
}) => {
  const { t } = useTranslation();
  const axlesConfigurationFields =
    permitApplication.getFieldsBySection('axlesConfiguration');
  if (axlesConfigurationFields.size === 0) return null;

  const overallDimensionsFields =
    axlesConfigurationFields.get('overallDimensions');

  const axlesFields = axlesConfigurationFields.get('axles');
  const displayAxles = !!axlesFields?.length;
  const lastUpdated = axlesFields
    ? hasUpdatedFields(axlesFields, order)
    : undefined;

  const rawTractorAxles = permitApplication.getFieldValue(
    TractorAxlesFormUtils.fieldName,
  );
  const tractorAxles = rawTractorAxles
    ? rawTractorAxles[AxleFormName as keyof EntryValue]
    : [];
  const trailers = permitApplication.getFieldValue(TrailersFormName);
  const trailerAxles = Array.isArray(trailers)
    ? trailers.reduce((acc, trailer) => {
        const trailerAxles = trailer[AxleFormName];
        if (Array.isArray(trailerAxles)) {
          return [...acc, ...trailerAxles];
        }
        return acc;
      }, [])
    : [];
  const allAxles = [...tractorAxles, ...trailerAxles];
  return (
    <CategorySection
      title={t(`orderSections.axlesConfiguration`)}
      requiredFields={Array.from(axlesConfigurationFields.values()).flat()}
      permitApplication={permitApplication}
      order={order}
      subsections={[
        {
          label: (
            <>
              {t('axles')}
              {lastUpdated && (
                <UpdateIcon
                  sx={{
                    fontSize: '1.4em',
                    verticalAlign: 'middle',
                    color: colors.tertiary['60'],
                  }}
                />
              )}
            </>
          ),
          renderFields: () =>
            displayAxles && axlesFields ? (
              <ReadOnlyAxlesTable
                isMetric={isMetric}
                key={`axle-of-permit-${permitApplication.id}`}
                name="axles"
                fields={axlesFields}
                order={order}
              >
                {allAxles.map((axle, i) => (
                  <ReadOnlyAxlesRow
                    key={axle.id ?? `${permitApplication.id}-axle-row-${i}`}
                    fields={axlesFields}
                    axleIndex={i}
                    axle={axle}
                    isMetric={isMetric}
                  />
                ))}
              </ReadOnlyAxlesTable>
            ) : null,
        },
        {
          label: t('overallDimensions'),
          fields: overallDimensionsFields,
        },
      ]}
    />
  );
};

const hasUpdatedFields = (axlesFields: string[], order: Order) => {
  return (
    axlesFields.map((field) => order.getLastUpdatedFor(field)).filter(Boolean)
      .length > 0
  );
};
